import { CrescLoader } from "../components/LoadingScreen";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, RefreshCw, X, Check, Search, Layers, GitBranch, Zap, Flag, Users, CheckSquare } from "lucide-react";
import { GoogleCalendarIcon, MicrosoftIcon, GoogleDocsIcon, GoogleDriveIcon, SpotifyIcon, BooksIcon, ClickUpIcon, SlackIcon } from "../components/BrandIcons";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { toast } from "sonner";

/**
 * Library — central hub for managing all third-party integrations.
 *
 * Iter39 user request: "We need to organize integrations separately
 * into a tab called 'library', where users can setup and manage their
 * integrations."
 *
 * Lists all available providers, their connection status, and per-
 * provider connect/disconnect/import actions:
 *   - Google Calendar (calendar import; existing flow)
 *   - Microsoft Calendar (calendar import; existing flow)
 *   - Google Docs (document import — uses same OAuth client as Calendar)
 *   - Spotify (now-playing widget + library)
 *
 * Iter40 polish: each provider uses its own platform brand icon
 * instead of generic lucide icons.
 */

// Platform groups — services under the same account
const PLATFORMS = [
  {
    id: "google",
    label: "Google",
    icon: GoogleCalendarIcon,
    accent: "#4285F4",
    category: "Calendar & Documents",
    services: [
      { id: "google",      label: "Google Calendar", icon: GoogleCalendarIcon },
      { id: "google_docs", label: "Google Docs",     icon: GoogleDocsIcon },
      { id: "google_drive", label: "Google Drive",   icon: GoogleDriveIcon },
    ],
  },
  {
    id: "microsoft",
    label: "Microsoft",
    icon: MicrosoftIcon,
    accent: "#0078D4",
    category: "Calendar",
    services: [
      { id: "microsoft",   label: "Microsoft Calendar", icon: MicrosoftIcon },
      { id: "teams",       label: "Microsoft Teams",    icon: MicrosoftIcon },
      { id: "onedrive",    label: "OneDrive",           icon: MicrosoftIcon },
    ],
  },
];

const PROVIDERS = [
  { id: "google",      label: "Google Calendar",   icon: GoogleCalendarIcon, accent: "#4285F4", category: "Calendar",      platform: "google" },
  { id: "microsoft",   label: "Microsoft Calendar", icon: MicrosoftIcon,      accent: "#0078D4", category: "Calendar",      platform: "microsoft" },
  { id: "teams",       label: "Microsoft Teams",    icon: MicrosoftIcon,      accent: "#6264A7", category: "Productivity",   platform: "microsoft" },
  { id: "google_docs", label: "Google Docs",         icon: GoogleDocsIcon,     accent: "#4285F4", category: "Documents",     platform: "google" },
  { id: "google_drive",label: "Google Drive",        icon: GoogleDriveIcon,    accent: "#4285F4", category: "Documents",     platform: "google" },
  { id: "onedrive",    label: "OneDrive",            icon: MicrosoftIcon,      accent: "#0078D4", category: "Documents",     platform: "microsoft" },
  { id: "clickup",     label: "ClickUp",             icon: ClickUpIcon,        accent: "#7B68EE", category: "Productivity"  },
  { id: "slack",       label: "Slack",               icon: SlackIcon,          accent: "#E01E5A", category: "Productivity"  },
  { id: "spotify",     label: "Spotify",             icon: SpotifyIcon,        accent: "#1DB954", category: "Media"         },
];

function ResourceLibrarySection() {
  const { workspaces, current } = useWorkspace?.() || {};
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const wsId = current?.workspace_id;

  useEffect(() => {
    if (!wsId) return;
    setLoading(true);
    api.get(`/workspaces/${wsId}/events`).then(({ data }) => {
      const evs = (data?.events || data || []).filter(e => e.resource_url);
      setResources(evs.map(e => ({
        title: e.title,
        url: e.resource_url,
        kind: e.resource_kind || "link",
        event_id: e.event_id,
      })));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [wsId]);

  if (!wsId || (!loading && resources.length === 0)) return null;

  return (
    <section className="space-y-2 pt-2 border-t border-[#2A2A2A]">
      <div className="text-[10px] uppercase tracking-wider text-[#A1A1AA] mb-2">Resource Library</div>
      {loading ? (
        <div className="text-[11px] text-[#52525B]">Loading resources…</div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {resources.map((r, i) => (
            <a key={i} href={r.url} target="_blank" rel="noreferrer"
              className="flex items-center gap-3 bg-[#141414] border border-[#2A2A2A] rounded-lg px-3 py-2 hover:border-[#3A3A3A] transition-colors group">
              <span className="text-lg">{r.kind === "doc" ? "📄" : r.kind === "image" ? "🖼" : "🔗"}</span>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] text-[#EDEDED] truncate">{r.title}</div>
                <div className="text-[10px] text-[#52525B] truncate">{r.url}</div>
              </div>
            </a>
          ))}
        </div>
      )}
    </section>
  );
}

export default function Library() {
  const nav = useNavigate();
  const { current: ws, workspaces } = useWorkspace() || {};
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [subtab, setSubtab] = useState("integrations"); // "shards" | "integrations"
  const [docsListing, setDocsListing] = useState({ open: false, files: [], loading: false });
  const [importingId, setImportingId] = useState(null);
  const [clickupListing, setClickupListing] = useState({ open: false, tasks: [], loading: false });
  const [clickupActiveTeam, setClickupActiveTeam] = useState(null); // active team tab
  const [clickupSelected, setClickupSelected] = useState(new Set());
  const [clickupImporting, setClickupImporting] = useState(false);
  const [clickupTargetWs, setClickupTargetWs] = useState(null); // workspace to import into
  const [clickupTokenInput, setClickupTokenInput] = useState({ open: false, value: "", saving: false });
  const [slackListing, setSlackListing] = useState({ open: false, channels: [], messages: [], activeChannel: null, loading: false });
  const [teamsListing, setTeamsListing] = useState({ open: false, teams: [], channels: [], messages: [], activeTeam: null, activeChannel: null, loading: false });

  const refresh = async () => {
    setLoading(true);
    const results = await Promise.all(
      PROVIDERS.map(async (p) => {
        try {
          const { data } = await api.get(`/integrations/${p.id}/status`);
          return [p.id, data];
        } catch (err) {
          if (err?.response?.status === 503) {
            return [p.id, { connected: false, _not_configured: err.response.data?.detail }];
          }
          return [p.id, { connected: false }];
        }
      })
    );
    setStatuses(Object.fromEntries(results));
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    // Honour OAuth deep-link bounce-back.
    const url = new URL(window.location.href);
    const c = url.searchParams.get("integration_connected");
    if (c) {
      toast.success(`${PROVIDERS.find((p) => p.id === c)?.label || c} connected!`);
      url.searchParams.delete("integration_connected");
      window.history.replaceState({}, "", url.pathname + (url.searchParams.toString() ? "?" + url.searchParams.toString() : "") + url.hash);
    }
    const e = url.searchParams.get("integration_error");
    if (e) {
      toast.error(`Integration error: ${e}`);
      url.searchParams.delete("integration_error");
      window.history.replaceState({}, "", url.pathname + (url.searchParams.toString() ? "?" + url.searchParams.toString() : "") + url.hash);
    }
  }, []);

  const connect = async (p) => {
    try {
      const { data } = await api.get(`/integrations/${p.id}/authorize`);
      const popup = window.open(data.authorization_url, "cresc-oauth", "width=540,height=720");

      const onConnected = async (e) => {
        if (e.detail?.provider) {
          toast.success(`${PROVIDERS.find(x => x.id === e.detail.provider)?.label || e.detail.provider} connected!`);
        }
        cleanup();
        await refresh();
      };
      const onError = (e) => {
        toast.error(`Integration error: ${e.detail?.error || "unknown"}`);
        cleanup();
      };

      // Fallback poll in case popup closes without firing the event
      const poll = setInterval(async () => {
        if (!popup || popup.closed) {
          cleanup();
          await refresh();
        }
      }, 800);

      const cleanup = () => {
        clearInterval(poll);
        window.removeEventListener("crescendo:integration-connected", onConnected);
        window.removeEventListener("crescendo:integration-error", onError);
      };

      window.addEventListener("crescendo:integration-connected", onConnected);
      window.addEventListener("crescendo:integration-error", onError);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (typeof d === "object" && d?.error === "integration_not_configured") {
        toast.error(d.message);
      } else {
        toast.error("OAuth failed to start");
      }
    }
  };

  const disconnect = async (p) => {
    if (!window.confirm(`Disconnect ${p.label}?`)) return;
    await api.delete(`/integrations/${p.id}/disconnect`);
    toast.success("Disconnected");
    refresh();
  };

  const openDocsList = async () => {    setDocsListing({ open: true, files: [], loading: true });
    try {
      const { data } = await api.get("/integrations/google_docs/files");
      setDocsListing({ open: true, files: data || [], loading: false });
    } catch (err) {
      setDocsListing({ open: true, files: [], loading: false });
      toast.error(err?.response?.data?.detail || "Failed to list Google Docs");
    }
  };

  const importDoc = async (file) => {
    let activeWs = ws || (workspaces && workspaces[0]);
    if (!activeWs?.workspace_id) {
      try {
        const { data: newWs } = await api.post("/workspaces", { name: "Personal", color: "#0055FF" });
        activeWs = newWs;
      } catch { toast.error("Could not create a workspace."); return; }
    }
    setImportingId(file.id);
    try {
      await api.post(`/integrations/google_docs/import/${file.id}`, { workspace_id: activeWs.workspace_id });
      toast.success(`Imported "${file.name}"`);
      try { window.dispatchEvent(new CustomEvent("crescendo:docs-changed")); } catch {}
    } catch (err) {
      const det = err?.response?.data?.detail;
      const msg = typeof det === "string" ? det : Array.isArray(det) ? det.map(d => d.msg || JSON.stringify(d)).join("; ") : (det ? JSON.stringify(det) : "Import failed");
      toast.error(msg);
    }
    setImportingId(null);
  };

  const saveClickupPersonalToken = async () => {
    const token = clickupTokenInput.value.trim();
    if (!token.startsWith("pk_")) { toast.error("ClickUp personal tokens start with pk_"); return; }
    setClickupTokenInput(t => ({ ...t, saving: true }));
    try {
      await api.post("/integrations/clickup/personal-token", { token });
      toast.success("Personal token saved — tasks from all workspaces will now load.");
      setClickupTokenInput({ open: false, value: "", saving: false });
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? "Invalid token format." : (typeof detail === "string" ? detail : "Failed to save token");
      toast.error(msg);
      setClickupTokenInput(t => ({ ...t, saving: false }));
    }
  };

  const openDocsPicker = openDocsList; // alias for platform card
  const openClickUpTasks = async () => {
    setClickupTargetWs(ws?.workspace_id || null); // initialize to current workspace
    setClickupListing({ open: true, tasks: [], loading: true });
    setClickupSelected(new Set());
    try {
      const { data } = await api.get("/integrations/clickup/tasks");
      const tasks = data?.tasks || [];
      setClickupListing({ open: true, tasks, loading: false, debug: data?.debug });
      // Auto-select first team tab
      const teams = [...new Set(tasks.map(t => t.space_name || "General").filter(Boolean))];
      setClickupActiveTeam(teams[0] || null);
    } catch (err) {
      setClickupListing({ open: true, tasks: [], loading: false, debug: null });
      toast.error(err?.response?.data?.detail || "Failed to fetch ClickUp tasks");
    }
  };

  const importClickUpTasks = async () => {
    // Use explicitly selected workspace ID, fall back to current
    const wsId = clickupTargetWs || ws?.workspace_id || workspaces?.[0]?.workspace_id;
    if (!wsId) {
      toast.error("Please select a destination workspace.");
      return;
    }
    const activeWs = { workspace_id: wsId, name: workspaces?.find(w => w.workspace_id === wsId)?.name || ws?.name || "workspace" };
    if (clickupSelected.size === 0) { toast.error("Select at least one task to import."); return; }
    setClickupImporting(true);
    try {
      // Send full task objects — avoids per-task re-fetches in the backend that can silently fail
      const selectedTasks = clickupListing.tasks.filter(t => clickupSelected.has(t.id));
      if (!activeWs?.workspace_id) { toast.error("No workspace selected"); setClickupImporting(false); return; }
      const { data } = await api.post("/integrations/clickup/import", {
        task_ids: Array.from(clickupSelected),
        tasks: selectedTasks,
        workspace_id: activeWs.workspace_id,
      });
      toast.success(`Imported ${data.created} task${data.created !== 1 ? "s" : ""} into "${activeWs.name}"`);
      setClickupListing(l => ({ ...l, open: false }));
      setClickupSelected(new Set());
      try { window.dispatchEvent(new CustomEvent("crescendo:events-changed")); } catch {}
    } catch (err) {
      const det = err?.response?.data?.detail;
      const msg = typeof det === "string" ? det : Array.isArray(det) ? det.map(d => d.msg || JSON.stringify(d)).join("; ") : (det ? JSON.stringify(det) : "Import failed");
      toast.error(msg);
    }
    setClickupImporting(false);
  };

  const openSlackChannels = async () => {
    setSlackListing({ open: true, channels: [], messages: [], activeChannel: null, loading: true });
    try {
      const { data } = await api.get("/integrations/slack/channels");
      setSlackListing(s => ({ ...s, channels: data || [], loading: false }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load Slack channels");
      setSlackListing(s => ({ ...s, loading: false }));
    }
  };

  const loadSlackMessages = async (channelId) => {
    setSlackListing(s => ({ ...s, activeChannel: channelId, messages: [], loading: true }));
    try {
      const { data } = await api.get(`/integrations/slack/channels/${channelId}/messages`);
      setSlackListing(s => ({ ...s, messages: data || [], loading: false }));
    } catch {
      setSlackListing(s => ({ ...s, loading: false }));
    }
  };

  const importSlackMessage = async (msg) => {
    let activeWs = ws || (workspaces && workspaces[0]);
    if (!activeWs?.workspace_id) { toast.error("No active workspace"); return; }
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const nowIso = new Date().toISOString();
      const { data } = await api.post(`/workspaces/${activeWs.workspace_id}/events/detect-meeting`, {
        text: msg.text || msg.content || msg.message || "",
        source: "slack",
        client_now_iso: nowIso,
        client_tz: tz,
      });
      if (data?.detected) {
        toast.success(`Meeting "${data.event?.title}" added to calendar`);
      } else {
        // No meeting detected — fall back to creating a plain task
        const start = new Date(msg.datetime || Date.now());
        if (!isNaN(start.getTime())) start.setHours(start.getHours() + 1);
        const title = (msg.text || "").replace(/<[^>]+>/g, "").trim().slice(0, 80) || "Slack message";
        await api.post(`/workspaces/${activeWs.workspace_id}/events`, {
          title, type: "task",
          start: (isNaN(start.getTime()) ? new Date() : start).toISOString(),
          end: new Date((isNaN(start.getTime()) ? Date.now() : start.getTime()) + 3600000).toISOString(),
        });
        toast.success("Imported as task (no meeting details detected)");
      }
      window.dispatchEvent(new CustomEvent("crescendo:events-changed"));
    } catch (err) {
      const det = err?.response?.data?.detail;
      const msg = typeof det === "string" ? det : Array.isArray(det) ? det.map(d => d.msg || JSON.stringify(d)).join("; ") : (det ? JSON.stringify(det) : "Import failed");
      toast.error(msg);
    }
  };

  const openTeams = async () => {
    setTeamsListing({ open: true, teams: [], channels: [], messages: [], activeTeam: null, activeChannel: null, loading: true });
    try {
      const { data } = await api.get("/integrations/teams/teams");
      setTeamsListing(s => ({ ...s, teams: data || [], loading: false }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load Teams");
      setTeamsListing(s => ({ ...s, loading: false }));
    }
  };

  const loadTeamsChannels = async (teamId) => {
    setTeamsListing(s => ({ ...s, activeTeam: teamId, channels: [], messages: [], activeChannel: null, loading: true }));
    try {
      const { data } = await api.get(`/integrations/teams/teams/${teamId}/channels`);
      setTeamsListing(s => ({ ...s, channels: data || [], loading: false }));
    } catch { setTeamsListing(s => ({ ...s, loading: false })); }
  };

  const loadTeamsMessages = async (teamId, channelId) => {
    setTeamsListing(s => ({ ...s, activeChannel: channelId, messages: [], loading: true }));
    try {
      const { data } = await api.get(`/integrations/teams/teams/${teamId}/channels/${channelId}/messages`);
      setTeamsListing(s => ({ ...s, messages: data || [], loading: false }));
    } catch { setTeamsListing(s => ({ ...s, loading: false })); }
  };

  const importTeamsMessage = async (msg) => {
    let activeWs = ws || (workspaces && workspaces[0]);
    if (!activeWs?.workspace_id) { toast.error("No active workspace"); return; }
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const { data } = await api.post(`/workspaces/${activeWs.workspace_id}/events/detect-meeting`, {
        text: msg.text || msg.content || msg.message || "", source: "teams",
        client_now_iso: new Date().toISOString(), client_tz: tz,
      });
      if (data?.detected) {
        toast.success(`Meeting "${data.event?.title}" added to calendar`);
      } else {
        const start = new Date(msg.datetime || Date.now());
        if (!isNaN(start.getTime())) start.setHours(start.getHours() + 1);
        await api.post(`/workspaces/${activeWs.workspace_id}/events`, {
          title: (msg.text || "").slice(0, 80) || "Teams message", type: "task",
          start: (isNaN(start.getTime()) ? new Date() : start).toISOString(),
          end: new Date((isNaN(start.getTime()) ? Date.now() : start.getTime()) + 3600000).toISOString(),
        });
        toast.success("Imported as task (no meeting details detected)");
      }
      window.dispatchEvent(new CustomEvent("crescendo:events-changed"));
    } catch (err) { const det = err?.response?.data?.detail;
      const msg = typeof det === "string" ? det : Array.isArray(det) ? det.map(d => d.msg || JSON.stringify(d)).join("; ") : (det ? JSON.stringify(det) : "Import failed");
      toast.error(msg); }
  };

  const PRIORITY_COLORS = { urgent: "#EF4444", high: "#FF8A00", normal: "#5A90F5", medium: "#5A90F5", low: "#71717A" };
  const STATUS_COLORS = { done: "#00C781", "in progress": "#5A90F5", blocked: "#EF4444" };

  const groups = {};
  for (const p of PROVIDERS) {
    groups[p.category] = groups[p.category] || [];
    groups[p.category].push(p);
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED]" data-testid="library-page">
      <header className="border-b border-[#2A2A2A] px-6 py-3 flex items-center justify-between gap-4">
        <button onClick={() => nav("/app")} className="text-xs text-[#A1A1AA] hover:text-[#EDEDED] inline-flex items-center gap-1 flex-shrink-0" data-testid="library-back">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <div className="flex items-center gap-2 text-xs text-[#A1A1AA] flex-shrink-0">
          <Layers className="w-3.5 h-3.5 text-[#5A90F5]" /> Fragments
        </div>
        {/* Search bar */}
        <div className="flex-1 max-w-sm flex items-center gap-2 bg-[#141414] border border-[#2A2A2A] rounded-xl px-3 py-1.5">
          <Search className="w-3.5 h-3.5 text-[#52525B] flex-shrink-0" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search fragments…"
            className="flex-1 bg-transparent text-[12px] text-[#EDEDED] placeholder-[#52525B] outline-none" />
          {search && <button onClick={() => setSearch("")} className="text-[#52525B] hover:text-[#A1A1AA]"><X className="w-3 h-3" /></button>}
        </div>
        <button onClick={refresh} className="text-xs text-[#A1A1AA] hover:text-[#EDEDED] inline-flex items-center gap-1 flex-shrink-0" data-testid="library-refresh">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      {/* Subtabs */}
      <div className="border-b border-[#2A2A2A] px-6 flex gap-1 pt-2">
        {[["shards","Shards"],["integrations","Integrations"]].map(([id, label]) => (
          <button key={id} onClick={() => setSubtab(id)}
            className={`px-4 py-2 text-[12px] font-medium border-b-2 transition-colors ${subtab === id ? "border-[#5A90F5] text-[#5A90F5]" : "border-transparent text-[#71717A] hover:text-[#A1A1AA]"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Shards tab */}
      {subtab === "shards" && (
        <main className="max-w-3xl mx-auto p-6">
          <p className="text-[12px] text-[#71717A] mb-6">Shards are feature elements you can pin and access quickly. Choose which Crescendo features to include in your Fragments panel.</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { id: "ab",          label: "A/B Decisions",    icon: GitBranch,   color: "#14B8A6", path: "/ab",          desc: "Decision tracking with branching options" },
              { id: "automations", label: "Automations",      icon: Zap,         color: "#FF8A00", path: "/",            desc: "Free-form workflow canvas with dependencies" },
              { id: "boards",      label: "Progress Boards",  icon: Flag,        color: "#5A90F5", path: "/boards",      desc: "Linear sequential task flows" },
              { id: "directory",   label: "Directory",        icon: Users,       color: "#A855F7", path: "/directory",   desc: "Contacts with relationship health tracking" },
              { id: "commitments", label: "Commitments",      icon: CheckSquare, color: "#00C781", path: "/commitments", desc: "Promises, contracts, and policies" },
              { id: "capture",     label: "Capture",          icon: Zap,         color: "#F59E0B", path: "/capture",     desc: "Quick idea fragments and unrefined thoughts" },
            ].filter(s => !search || s.label.toLowerCase().includes(search.toLowerCase()) || s.desc.toLowerCase().includes(search.toLowerCase()))
            .map(shard => {
              const Icon = shard.icon;
              return (
                <button key={shard.id} onClick={() => nav(shard.path)}
                  className="flex items-start gap-3 p-4 bg-[#111] border border-[#2A2A2A] rounded-xl hover:border-[#3A3A3A] text-left transition-all group">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: shard.color + "20" }}>
                    <Icon className="w-4 h-4" style={{ color: shard.color }} />
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-[#EDEDED] group-hover:text-white">{shard.label}</p>
                    <p className="text-[11px] text-[#71717A] mt-0.5">{shard.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </main>
      )}

      {/* Integrations tab */}
      {subtab === "integrations" && (
      <main className="max-w-3xl mx-auto p-6 space-y-6">
        {loading && Object.keys(statuses).length === 0 ? (
          // Skeleton loading state
          <div className="space-y-6">
            {["Calendar", "Documents", "Productivity", "Media"].map(cat => (
              <section key={cat} className="space-y-2">
                <div className="text-[10px] uppercase tracking-wider text-[#A1A1AA] mb-2">{cat}</div>
                {[1, cat === "Calendar" ? 2 : 1].map(i => (
                  <div key={i} className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-4 flex items-center gap-4 animate-pulse">
                    <div className="w-10 h-10 rounded-lg bg-[#222] flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-[#222] rounded w-32" />
                      <div className="h-2 bg-[#1A1A1A] rounded w-48" />
                    </div>
                    <div className="h-7 w-20 bg-[#1A1A1A] rounded" />
                  </div>
                ))}
              </section>
            ))}
          </div>
        ) : (
        <>{/* Platform-grouped cards first (Google, Microsoft) */}
          {PLATFORMS.filter(plat => !search || plat.label.toLowerCase().includes(search.toLowerCase()) || plat.services.some(s => s.label.toLowerCase().includes(search.toLowerCase()))).map(plat => {
            const anyConnected = plat.services.some(svc => statuses[svc.id]?.connected);
            const allStatuses = plat.services.map(svc => ({ ...svc, s: statuses[svc.id] || {} }));
            const firstConnected = allStatuses.find(x => x.s.connected);
            const Icon = plat.icon;
            return (
              <section key={plat.id} className="space-y-2">
                <div className="text-[10px] uppercase tracking-wider text-[#A1A1AA] mb-2">{plat.category}</div>
                <div className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-4" data-testid={`library-platform-${plat.id}`}>
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#1a1a1a] flex-shrink-0">
                      <Icon className="w-7 h-7" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold">{plat.label}</div>
                      <div className="text-[10px] text-[#A1A1AA]">
                        {anyConnected ? `Connected as ${firstConnected?.s.email || firstConnected?.s.name || "—"}` : "Not connected"}
                      </div>
                    </div>
                    {!anyConnected ? (
                      <button
                        onClick={() => connect(PROVIDERS.find(p => p.id === plat.services[0].id))}
                        className="text-[11px] px-4 py-2 rounded-lg font-semibold text-white"
                        style={{ background: "#5A90F5" }}
                        data-testid={`library-connect-${plat.id}`}
                      >Connect</button>
                    ) : (
                      <button
                        onClick={() => {
                          plat.services.forEach(svc => {
                            const s = statuses[svc.id] || {};
                            if (s.connected) disconnect(PROVIDERS.find(p => p.id === svc.id));
                          });
                        }}
                        className="text-[11px] px-4 py-2 rounded-lg border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10"
                        data-testid={`library-disconnect-${plat.id}`}
                      >Disconnect</button>
                    )}
                  </div>
                  {/* Service checkbox list */}
                  <div className="divide-y divide-[#2A2A2A] border-t border-[#2A2A2A] pt-2">
                    {allStatuses.map(({ id, label, icon: SvcIcon, s }) => {
                      const provider = PROVIDERS.find(p => p.id === id);
                      return (
                        <div key={id} className="flex items-center gap-3 py-2" data-testid={`library-svc-${id}`}>
                          <input
                            type="checkbox"
                            checked={!!s.connected}
                            onChange={() => s.connected ? disconnect(provider) : connect(provider)}
                            className="accent-[#5A90F5] w-3.5 h-3.5 flex-shrink-0 cursor-pointer"
                          />
                          <SvcIcon className="w-3.5 h-3.5 flex-shrink-0 text-[#A1A1AA]" />
                          <span className={`text-[11px] flex-1 ${s.connected ? "text-[#EDEDED]" : "text-[#71717A]"}`}>{label}</span>
                          {s.connected && (
                            <div className="flex items-center gap-1.5">
                              {id === "google_docs" && <button onClick={openDocsPicker} className="text-[10px] px-2 py-1 rounded border border-[#2A2A2A] text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#1a1a1a]">Import</button>}
                              {id === "microsoft" && <button onClick={() => setImportOpen?.(true)} className="text-[10px] px-2 py-1 rounded border border-[#2A2A2A] text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#1a1a1a]">Import</button>}
                              {id === "teams" && <button onClick={openTeams} className="text-[10px] px-2 py-1 rounded border border-[#2A2A2A] text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#1a1a1a]">Browse</button>}
                              {id === "slack" && <button onClick={async () => {
                                try {
                                  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                                  const { data } = await api.post("/integrations/slack/poll-meetings");
                                  if (data.created > 0) toast.success(`Found ${data.created} meeting${data.created !== 1 ? "s" : ""} in Slack`);
                                  else toast.info("No new meetings found in recent Slack messages");
                                  window.dispatchEvent(new CustomEvent("crescendo:events-changed"));
                                } catch (err) { toast.error(err?.response?.data?.detail || "Sync failed"); }
                              }} className="text-[10px] px-2 py-1 rounded border border-[#E01E5A]/30 text-[#E01E5A] hover:bg-[#E01E5A]/10">Sync Meetings</button>}
                              <button onClick={() => disconnect(provider)} className="text-[10px] px-2 py-1 rounded border border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10">Remove</button>
                            </div>
                          )}
                          {!s.connected && (
                            <button onClick={() => connect(provider)} className="text-[10px] px-2 py-1 rounded border border-[#2A2A2A] text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#1a1a1a]">Connect</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            );
          })}
          {/* Standalone providers (not grouped under a platform) */}
          {Object.entries(groups).filter(([cat]) => !PLATFORMS.some(p => p.category === cat)).map(([cat, items]) => (
            items.filter(p => !p.platform).length === 0 ? null : (
          <section key={cat} className="space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-[#A1A1AA] mb-2">{cat}</div>
            {items.filter(p => !p.platform).map((p) => {
              const s = statuses[p.id] || {};
              const Icon = p.icon;
              const notConfigured = s._not_configured;
              return (
                <div
                  key={p.id}
                  className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-4 flex items-center gap-4"
                  data-testid={`library-provider-${p.id}`}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#1a1a1a]"
                  >
                    <Icon className="w-7 h-7" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#EDEDED]">{p.label}</div>
                    <div className="text-[10px] text-[#A1A1AA] truncate">
                      {notConfigured ? "Not configured (admin must set OAuth keys)"
                        : s.connected
                          ? (() => {
                              const name = s.name || "";
                              const email = s.email || "";
                              if (p.id === "spotify" || p.id === "clickup") {
                                const display = name || email.split("@")[0] || "—";
                                const needsReconnect = !name && email;
                                return needsReconnect
                                  ? <span className="flex items-center gap-1.5">{display} <button onClick={() => connect(p)} className="text-[10px] text-[#5A90F5] underline hover:no-underline">Reconnect to show name</button></span>
                                  : `Connected as ${display}`;
                              }
                              if (p.id === "slack") {
                                return `Connected${name ? ` as ${name}` : email ? ` as ${email}` : ""}`;
                              }
                              return `Connected as ${email || name || "—"}`;
                            })()
                        : "Not connected"}
                    </div>
                  </div>
                  {/* Provider-specific actions */}
                  {p.id === "google_docs" && s.connected && (
                    <button
                      onClick={openDocsList}
                      className="text-[10px] px-3 py-1.5 rounded border border-[#2A2A2A] text-[#EDEDED] hover:bg-[#1a1a1a]"
                      data-testid="library-google-docs-list"
                    >Browse & Import</button>
                  )}
                  {p.id === "clickup" && s.connected && (
                    <button
                      onClick={openClickUpTasks}
                      className="text-[10px] px-3 py-1.5 rounded border border-[#2A2A2A] text-[#EDEDED] hover:bg-[#1a1a1a]"
                    >Browse & Import</button>
                  )}
                  {p.id === "slack" && s.connected && (
                    <button
                      onClick={openSlackChannels}
                      className="text-[10px] px-3 py-1.5 rounded border border-[#2A2A2A] text-[#EDEDED] hover:bg-[#1a1a1a]"
                      data-testid="library-slack-browse"
                    >Browse Messages</button>
                  )}
                  {p.id === "teams" && s.connected && (
                    <button
                      onClick={openTeams}
                      className="text-[10px] px-3 py-1.5 rounded border border-[#2A2A2A] text-[#EDEDED] hover:bg-[#1a1a1a]"
                      data-testid="library-teams-browse"
                    >Browse Messages</button>
                  )}
                  {/* Connection toggles */}
                  {s.connected ? (
                    <button
                      onClick={() => disconnect(p)}
                      className="text-[10px] px-3 py-1.5 rounded border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10"
                      data-testid={`library-disconnect-${p.id}`}
                    >Disconnect</button>
                  ) : (
                    <button
                      onClick={() => connect(p)}
                      disabled={!!notConfigured}
                      className="text-[10px] px-3 py-1.5 rounded text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: "#5A90F5" }}
                      data-testid={`library-connect-${p.id}`}
                    >Connect</button>
                  )}
                </div>
              );
            })}
          </section>
            )
          ))}</>
        )}
        {/* Resource Library — aggregated view of all event-attached resources */}
        <ResourceLibrarySection />
      </main>
      )}

      {/* Google Docs picker modal */}
      {docsListing.open && (
        <div
          className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setDocsListing({ open: false, files: [], loading: false })}
          data-testid="google-docs-picker"
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-lg w-full max-w-xl max-h-[80vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#2A2A2A]">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <GoogleDocsIcon className="w-4 h-4" /> Google Docs
              </div>
              <button onClick={() => setDocsListing({ open: false, files: [], loading: false })} className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {docsListing.loading && (
                <div className="p-6 text-[11px] text-[#A1A1AA] flex items-center justify-center gap-1.5"><CrescLoader size={12} /> Loading…</div>
              )}
              {!docsListing.loading && docsListing.files.length === 0 && (
                <div className="p-6 text-[11px] text-[#A1A1AA] text-center">No Google Docs found.</div>
              )}
              {docsListing.files.map((f) => (
                <div key={f.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-[#2A2A2A] hover:bg-[#1a1a1a]" data-testid={`google-docs-file-${f.id}`}>
                  <FileText className="w-3.5 h-3.5 text-[#4285F4] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[#EDEDED] truncate">{f.name}</div>
                    <div className="text-[10px] text-[#A1A1AA]">Modified {f.modifiedTime ? new Date(f.modifiedTime).toLocaleString() : "—"}</div>
                  </div>
                  <button
                    onClick={() => importDoc(f)}
                    disabled={importingId === f.id}
                    className="text-[10px] px-2.5 py-1 rounded bg-[#5A90F5] text-white hover:bg-[#4a80e5] font-semibold disabled:opacity-60"
                    data-testid={`google-docs-import-${f.id}`}
                  >
                    {importingId === f.id ? "Importing…" : "Import"}
                  </button>
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-[#2A2A2A] text-[10px] text-[#A1A1AA]">
              Imports as a new doc in <span className="text-[#EDEDED]">{ws?.name || "current workspace"}</span>. Formatting + images preserved.
            </div>
          </div>
        </div>
      )}
      {/* ClickUp task picker modal */}
      {teamsListing.open && (
        <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4" onClick={() => setTeamsListing(s => ({ ...s, open: false }))}>
          <div className="bg-[#141414] border border-[#2A2A2A] rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#2A2A2A]">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold">
                  {teamsListing.activeChannel ? "Messages" : teamsListing.activeTeam ? "Channels" : "Teams"}
                </h3>
                <span className="text-[10px] text-[#6264A7] bg-[#6264A7]/15 px-1.5 py-0.5 rounded">Teams</span>
              </div>
              <div className="flex items-center gap-2">
                {(teamsListing.activeTeam || teamsListing.activeChannel) && (
                  <button onClick={() => {
                    if (teamsListing.activeChannel) setTeamsListing(s => ({ ...s, activeChannel: null, messages: [] }));
                    else setTeamsListing(s => ({ ...s, activeTeam: null, channels: [] }));
                  }} className="text-[11px] text-[#A1A1AA] hover:text-[#EDEDED]">← Back</button>
                )}
                <button onClick={() => setTeamsListing(s => ({ ...s, open: false }))} className="text-[#A1A1AA] hover:text-[#EDEDED]">
                  <svg width="12" height="12" viewBox="0 0 12 12"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-1">
              {teamsListing.loading && <div className="text-xs text-[#A1A1AA] text-center py-8">Loading…</div>}
              {/* Teams list */}
              {!teamsListing.loading && !teamsListing.activeTeam && teamsListing.teams.map(t => (
                <button key={t.id} onClick={() => loadTeamsChannels(t.id)}
                  className="w-full text-left px-3 py-2 rounded hover:bg-[#1a1a1a] flex items-center gap-2 text-sm">
                  <div className="w-6 h-6 rounded bg-[#6264A7]/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-[9px] text-[#6264A7] font-bold">{t.name[0]}</span>
                  </div>
                  <span className="text-[#EDEDED]">{t.name}</span>
                </button>
              ))}
              {!teamsListing.loading && !teamsListing.activeTeam && !teamsListing.teams.length && (
                <div className="text-xs text-[#A1A1AA] text-center py-8">No teams found.</div>
              )}
              {/* Channels list */}
              {!teamsListing.loading && teamsListing.activeTeam && !teamsListing.activeChannel && teamsListing.channels.map(c => (
                <button key={c.id} onClick={() => loadTeamsMessages(teamsListing.activeTeam, c.id)}
                  className="w-full text-left px-3 py-2 rounded hover:bg-[#1a1a1a] flex items-center gap-2 text-sm">
                  <span className="text-[#52525B]">#</span>
                  <span className="text-[#EDEDED]">{c.name}</span>
                </button>
              ))}
              {!teamsListing.loading && teamsListing.activeTeam && !teamsListing.activeChannel && !teamsListing.channels.length && (
                <div className="text-xs text-[#A1A1AA] text-center py-8">No channels found.</div>
              )}
              {/* Messages */}
              {!teamsListing.loading && teamsListing.activeChannel && teamsListing.messages.map(msg => (
                <div key={msg.id} className="flex items-start gap-3 px-3 py-2 rounded hover:bg-[#1a1a1a] group">
                  <div className="flex-1 min-w-0">
                    {msg.sender && <div className="text-[10px] text-[#5A90F5] mb-0.5">{msg.sender}</div>}
                    <div className="text-xs text-[#EDEDED] line-clamp-2">{msg.text}</div>
                    <div className="text-[10px] text-[#52525B] mt-0.5">{msg.datetime ? new Date(msg.datetime).toLocaleString() : ""}</div>
                  </div>
                  <button onClick={() => importTeamsMessage(msg)}
                    className="opacity-0 group-hover:opacity-100 text-[10px] px-2 py-1 rounded bg-[#6264A7] text-white flex-shrink-0">
                    Import
                  </button>
                </div>
              ))}
              {!teamsListing.loading && teamsListing.activeChannel && !teamsListing.messages.length && (
                <div className="text-xs text-[#A1A1AA] text-center py-8">No messages found.</div>
              )}
            </div>
          </div>
        </div>
      )}
      {slackListing.open && (
        <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4" onClick={() => setSlackListing(s => ({ ...s, open: false }))}>
          <div className="bg-[#141414] border border-[#2A2A2A] rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#2A2A2A]">
              <h3 className="text-sm font-semibold">{slackListing.activeChannel ? "Messages" : "Slack Channels"}</h3>
              <div className="flex gap-2">
                {slackListing.activeChannel && (
                  <button onClick={() => setSlackListing(s => ({ ...s, activeChannel: null, messages: [] }))} className="text-[11px] text-[#A1A1AA] hover:text-[#EDEDED]">← Back</button>
                )}
                <button onClick={() => setSlackListing(s => ({ ...s, open: false }))} className="text-[#A1A1AA] hover:text-[#EDEDED]">
                  <svg width="12" height="12" viewBox="0 0 12 12"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {slackListing.loading && <div className="text-xs text-[#A1A1AA] text-center py-8">Loading…</div>}
              {!slackListing.loading && !slackListing.activeChannel && slackListing.channels.map(ch => (
                <button key={ch.id} onClick={() => loadSlackMessages(ch.id)}
                  className="w-full text-left px-3 py-2 rounded hover:bg-[#1a1a1a] flex items-center gap-2 text-sm">
                  <span className="text-[#52525B]">#</span>
                  <span className="text-[#EDEDED]">{ch.name}</span>
                  {ch.is_private && <span className="text-[9px] text-[#A1A1AA] bg-[#1a1a1a] px-1.5 py-0.5 rounded">private</span>}
                </button>
              ))}
              {!slackListing.loading && !slackListing.activeChannel && slackListing.channels.length === 0 && (
                <div className="text-xs text-[#A1A1AA] text-center py-8">No channels found. Make sure the app is added to channels.</div>
              )}
              {!slackListing.loading && slackListing.activeChannel && slackListing.messages.map(msg => (
                <div key={msg.ts} className="flex items-start gap-3 px-3 py-2 rounded hover:bg-[#1a1a1a] group">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[#EDEDED] line-clamp-2">{msg.text}</div>
                    <div className="text-[10px] text-[#52525B] mt-0.5">{new Date(msg.datetime).toLocaleString()}</div>
                  </div>
                  <button onClick={() => importSlackMessage(msg)}
                    className="opacity-0 group-hover:opacity-100 text-[10px] px-2 py-1 rounded bg-[#5A90F5] text-white flex-shrink-0">
                    Import
                  </button>
                </div>
              ))}
              {!slackListing.loading && slackListing.activeChannel && slackListing.messages.length === 0 && (
                <div className="text-xs text-[#A1A1AA] text-center py-8">No messages found.</div>
              )}
            </div>
          </div>
        </div>
      )}
      {clickupListing.open && (() => {
        // Group tasks by space/team name
        const allTasks = clickupListing.tasks;
        const teams = [...new Set(allTasks.map(t => t.space_name || "General").filter(Boolean))];
        const visibleTasks = clickupActiveTeam
          ? allTasks.filter(t => (t.space_name || "General") === clickupActiveTeam)
          : allTasks;
        return (
        <div
          className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setClickupListing({ open: false, tasks: [], loading: false })}
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#2A2A2A]">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <ClickUpIcon className="w-4 h-4" /> ClickUp Tasks
                {clickupSelected.size > 0 && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#7B68EE]/20 text-[#7B68EE] border border-[#7B68EE]/30">
                    {clickupSelected.size} selected
                  </span>
                )}
              </div>
              <button onClick={() => setClickupListing({ open: false, tasks: [], loading: false })} className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Team tabs */}
            {!clickupListing.loading && teams.length > 1 && (
              <div className="flex items-center gap-0 px-4 border-b border-[#2A2A2A] overflow-x-auto">
                {teams.map(team => (
                  <button key={team} onClick={() => { setClickupActiveTeam(team); setClickupSelected(new Set()); }}
                    className={`px-3 py-2 text-[11px] border-b-2 flex-shrink-0 transition-colors -mb-px ${
                      clickupActiveTeam === team
                        ? "border-[#7B68EE] text-[#EDEDED]"
                        : "border-transparent text-[#52525B] hover:text-[#A1A1AA]"
                    }`}
                  >{team}</button>
                ))}
              </div>
            )}

            <div className="flex-1 overflow-y-auto">
              {clickupListing.loading && (
                <div className="p-6 text-[11px] text-[#A1A1AA] flex items-center justify-center gap-1.5"><CrescLoader size={12} /> Fetching tasks…</div>
              )}
              {!clickupListing.loading && allTasks.length === 0 && (
                <div className="p-6 text-center">
                  <p className="text-[11px] text-[#A1A1AA] mb-2">No tasks found in ClickUp.</p>
                  {clickupListing.debug && (
                    <div className="text-[10px] text-[#52525B] max-w-xs mx-auto leading-relaxed text-left space-y-1 font-mono">
                      {clickupListing.debug.split(" | ").map((step, i) => (
                        <div key={i} className={step.includes("Error") || step.includes("error") ? "text-[#FF3B30]" : ""}>{step}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {!clickupListing.loading && visibleTasks.length > 0 && (
                <div className="px-4 py-2 border-b border-[#1A1A1A] flex items-center gap-2">
                  <button
                    onClick={() => {
                      const visibleIds = new Set(visibleTasks.map(t => t.id));
                      const allVisible = visibleTasks.every(t => clickupSelected.has(t.id));
                      setClickupSelected(prev => {
                        const next = new Set(prev);
                        if (allVisible) visibleIds.forEach(id => next.delete(id));
                        else visibleIds.forEach(id => next.add(id));
                        return next;
                      });
                    }}
                    className="text-[10px] text-[#A1A1AA] hover:text-[#EDEDED] transition-colors"
                  >
                    {visibleTasks.every(t => clickupSelected.has(t.id)) ? "Deselect all" : "Select all"}
                  </button>
                  <span className="text-[#2A2A2A]">·</span>
                  <span className="text-[10px] text-[#52525B]">{visibleTasks.length} tasks{clickupActiveTeam ? ` in ${clickupActiveTeam}` : ""}</span>
                </div>
              )}
              {visibleTasks.map((t) => {
                const selected = clickupSelected.has(t.id);
                const statusColor = STATUS_COLORS[t.status?.toLowerCase()] || "#52525B";
                const priorityColor = PRIORITY_COLORS[t.priority?.toLowerCase()] || null;
                return (
                  <div
                    key={t.id}
                    onClick={() => setClickupSelected(prev => {
                      const next = new Set(prev);
                      selected ? next.delete(t.id) : next.add(t.id);
                      return next;
                    })}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-[#1A1A1A] cursor-pointer transition-colors ${selected ? "bg-[#7B68EE]/08" : "hover:bg-[#141414]"}`}
                  >
                    <div className={`w-4 h-4 rounded flex-shrink-0 mt-0.5 border flex items-center justify-center transition-all ${selected ? "bg-[#7B68EE] border-[#7B68EE]" : "border-[#2A2A2A]"}`}>
                      {selected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-[#EDEDED] truncate mb-1">{t.name}</div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {t.space_name && (
                          <span className="text-[10px] text-[#52525B]">{t.space_name} › {t.list_name}</span>
                        )}
                        {t.status && (
                          <span className="text-[10px] flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusColor }} />
                            <span style={{ color: statusColor }}>{t.status}</span>
                          </span>
                        )}
                        {t.priority && (
                          <span className="text-[10px]" style={{ color: priorityColor || "#52525B" }}>
                            {t.priority}
                          </span>
                        )}
                        {t.due_date && (
                          <span className="text-[10px] text-[#52525B]">
                            Due {new Date(Number(t.due_date)).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-5 py-3 border-t border-[#2A2A2A] flex items-center gap-3 flex-wrap">
              {/* Workspace picker */}
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-[10px] text-[#A1A1AA] flex-shrink-0">Import into:</span>
                <select
                  value={clickupTargetWs || ws?.workspace_id || ""}
                  onChange={(e) => setClickupTargetWs(e.target.value)}
                  className="flex-1 min-w-0 bg-[#141414] border border-[#2A2A2A] rounded px-2 py-1 text-[11px] text-[#EDEDED] outline-none focus:border-[#7B68EE]"
                >
                  {!clickupTargetWs && !ws?.workspace_id && (
                    <option value="" disabled>Select workspace…</option>
                  )}
                  {(workspaces || (ws ? [ws] : [])).map(workspace => (
                    <option key={workspace.workspace_id} value={workspace.workspace_id}>{workspace.name}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={importClickUpTasks}
                disabled={clickupSelected.size === 0 || clickupImporting}
                className="text-[11px] px-4 py-1.5 rounded font-semibold text-white disabled:opacity-50 transition-all flex-shrink-0"
                style={{ background: "#7B68EE" }}
              >
                {clickupImporting ? "Importing…" : `Import ${clickupSelected.size > 0 ? clickupSelected.size : ""} Task${clickupSelected.size !== 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
      {/* ClickUp personal token modal */}
      {clickupTokenInput.open && (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setClickupTokenInput({ open: false, value: "", saving: false })}>
          <div onClick={e => e.stopPropagation()} className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-lg w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center gap-2 mb-4">
              <ClickUpIcon className="w-4 h-4" />
              <span className="text-sm font-semibold text-[#EDEDED]">Use Personal API Token</span>
            </div>
            <p className="text-[11px] text-[#71717A] mb-4 leading-relaxed">
              Your ClickUp OAuth token only sees one workspace. A personal token gives access to all your workspaces.
              Find it at <span className="text-[#5A90F5]">ClickUp → Settings → ClickUp API</span>.
            </p>
            <input
              type="text"
              placeholder="pk_12345_XXXXXXXXXXXX"
              value={clickupTokenInput.value}
              onChange={e => setClickupTokenInput(t => ({ ...t, value: e.target.value }))}
              className="w-full px-3 py-2 rounded bg-[#1A1A1A] border border-[#2A2A2A] text-[#EDEDED] text-xs font-mono mb-4 outline-none focus:border-[#5A90F5]"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setClickupTokenInput({ open: false, value: "", saving: false })}
                className="px-4 py-2 text-[11px] text-[#71717A] hover:text-[#EDEDED] transition-colors">Cancel</button>
              <button onClick={saveClickupPersonalToken} disabled={clickupTokenInput.saving}
                className="px-4 py-2 text-[11px] font-semibold text-white rounded disabled:opacity-50"
                style={{ background: "#7B68EE" }}>
                {clickupTokenInput.saving ? "Saving…" : "Save Token"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
